/**
 * Game end (SPEC §2.6): final leaderboard → superlatives → ranking history →
 * play again.
 *
 * Explicitly NOT here, and not to be added: a share card or export. That is a
 * privacy decision, not an omission (SPEC §6) — a shareable card either carries
 * twelve identifiable faces onto a public URL, which is exactly the data this
 * app is built never to persist, or it is a text list nobody shares. The
 * prohibition covers the ranking history below with the same force.
 */
import { useState } from 'react';
import {
  Button,
  Eyebrow,
  LeaderboardList,
  Screen,
  SuperlativeList,
  WaitingFor,
  RankingHistoryList,
} from '../../components/index.js';
import { ReviewTabs, type ReviewTab } from './ReviewTabs.js';
import {
  useIsHost,
  useLeaderboard,
  useRankingHistory,
  useRoomCode,
  useSelfId,
  useSuperlatives,
  useHostControls,
  formatNameList,
} from '../../store/index.js';
import { useLicensePlayers } from './RoundLeaderboardScreen.js';
import { clearAllRankingDrafts } from './rankingDraft.js';

export function SuperlativesScreen() {
  const leaderboard = useLeaderboard();
  const superlatives = useSuperlatives();
  const players = useLicensePlayers();
  const selfId = useSelfId();
  const isHost = useIsHost();
  const { playAgain } = useHostControls();
  const roomCode = useRoomCode();
  const history = useRankingHistory();
  const [tab, setTab] = useState<ReviewTab>('results');
  const [restarting, setRestarting] = useState(false);

  if (!leaderboard && !superlatives) {
    return (
      <Screen testId="superlatives-screen">
        <WaitingFor headline="Working out who embarrassed themselves most." />
      </Screen>
    );
  }

  /**
   * The switch is hidden when there is nothing to review, so the history pane
   * must never be the thing on screen when it is: a reconnect replays
   * `welcome` before it replays `superlatives`, which empties the history for a
   * frame, and a viewer sitting on the history tab would be left looking at
   * "no rankings" with no control to get back to the results. Falling back to
   * the results is always the honest thing to show.
   */
  const showHistory = tab === 'history' && history.length > 0;

  const winnerNames =
    leaderboard?.rows
      .filter((row) => row.rank === 1)
      .map((row) => players.get(row.playerId)?.name)
      .filter((name): name is string => Boolean(name)) ?? [];
  const resultTitle =
    winnerNames.length > 1
      ? `${formatNameList(winnerNames)} tied for first.`
      : winnerNames.length === 1
        ? `${winnerNames[0]} read the room best.`
        : 'Final standings.';

  return (
    <Screen
      eyebrow={<span className="text-green">Game over</span>}
      title={resultTitle}
      testId="superlatives-screen"
      bodyClassName="overflow-y-auto"
      footer={
        isHost ? (
          <Button
            full
            data-testid="play-again"
            disabled={restarting}
            onClick={() => {
              setRestarting(true);
              // Scores reset, so no turn's working state should survive.
              clearAllRankingDrafts(roomCode);
              playAgain();
            }}
          >
            Play again
          </Button>
        ) : (
          <p className="text-center text-[0.8125rem] text-ink-soft">The host can go again.</p>
        )
      }
    >
      {history.length > 0 && (
        <ReviewTabs tab={tab} onChange={setTab} historyCount={history.length} />
      )}

      {showHistory ? (
        <RankingHistoryList history={history} players={players} selfId={selfId ?? undefined} />
      ) : (
        <>
          {leaderboard && (
            <div className="pb-5">
              <LeaderboardList
                leaderboard={leaderboard}
                players={players}
                selfId={selfId ?? undefined}
                showRoundPoints={false}
                showScoringHint
              />
            </div>
          )}

          {superlatives && superlatives.length > 0 && (
            <>
              <Eyebrow className="pb-2">Highlights</Eyebrow>
              <SuperlativeList
                superlatives={superlatives}
                players={players}
                selfId={selfId ?? undefined}
              />
            </>
          )}
        </>
      )}
    </Screen>
  );
}
